import React, { useEffect, useState } from "react";
import { Layout } from "../../components/layout/Layout";
import { PageHeader } from "../../components/layout/PageHeader";
import LiveSession from "./scenario/LiveSession";
import {
  type AppConfig,
  type ScenarioKey,
  type DifficultyKey,
  SCENARIO_LABELS,
  SCENARIO_OPTIONS,
  DIFFICULTY_OPTIONS,
  getLearnerRoleForScenario,
} from "./scenario/types";
import { listScenarioDefinitions } from "../../services/api/scenarioLearning";

const defaultScenario = SCENARIO_OPTIONS[0];
const defaultConfig: AppConfig = {
  scenario: defaultScenario,
  learnerRole: getLearnerRoleForScenario(defaultScenario),
  difficulty: "easy",
};

export default function ScenarioConversationPage() {
  const [inSession, setInSession] = useState(false);
  const [config, setConfig] = useState<AppConfig>(defaultConfig);
  const [tenantScenarioCount, setTenantScenarioCount] = useState<number | null>(null);

  useEffect(() => {
    listScenarioDefinitions()
      .then((r) => setTenantScenarioCount(r.items?.length ?? 0))
      .catch(() => setTenantScenarioCount(null));
  }, []);

  if (inSession) {
    return (
      <Layout noPadding>
        <div className="h-full min-h-0 flex flex-col overflow-hidden">
          <LiveSession
            config={config}
            onEnd={() => setInSession(false)}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="h-full min-h-0 flex flex-col overflow-auto w-full">
        <PageHeader
          title="Scenario Conversation"
          subtitle={`Choose a scenario. You will have a 2-minute live Tamil conversation with the character. Unmute your mic to speak; the character will reply and gently correct your Tamil when needed.${
            tenantScenarioCount != null && tenantScenarioCount > 0
              ? ` School-specific scenarios loaded: ${tenantScenarioCount}.`
              : ""
          }`}
        />

        <div className="max-w-2xl w-full">
          <div className="space-y-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 md:p-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Scenario
              </label>
              <select
                value={config.scenario}
                onChange={(e) => {
                  const scenario = e.target.value as ScenarioKey;
                  setConfig((c) => ({
                    ...c,
                    scenario,
                    learnerRole: getLearnerRoleForScenario(scenario),
                  }));
                }}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 dark:focus:border-primary-400"
              >
                {SCENARIO_OPTIONS.map((key) => (
                  <option key={key} value={key}>
                    {SCENARIO_LABELS[key]}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Difficulty
              </label>
              <select
                value={config.difficulty}
                onChange={(e) =>
                  setConfig((c) => ({ ...c, difficulty: e.target.value as DifficultyKey }))
                }
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 dark:focus:border-primary-400"
              >
                {DIFFICULTY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={() => setInSession(true)}
              className="w-full mt-4 py-3 rounded-xl bg-primary-600 hover:bg-primary-500 dark:bg-primary-500 dark:hover:bg-primary-400 text-white font-bold text-base transition-colors"
            >
              Start conversation
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
